import { uniq } from "lodash";
import { useCallback, type ComponentProps, type FC } from "react";
import { Controller, useWatch, type Control, type UseFormSetValue } from "react-hook-form";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";

import { ProjectsAutoComplete } from "@ds/modules/settings/projects/components/projects-autocomplete";

import { useDetailsInlineSaving } from "@ds/components/details-view/hooks";
import { AutoCompleteMultipleDecorator } from "@ds/components/forms/autocomplete";
import { LabelWrapper } from "@ds/components/forms/labels";
import { TextLink } from "@ds/components/text-link";
import { useDeepRootSelector } from "@ds/hooks";
import { useOnMouseEventPreventDefaultHandler } from "@ds/utils/handlers";
import { composeDetailsRouteByEntityType } from "@ds/utils/router";

import { selectUsersByIds } from "../../../redux/selectors";

export const UserProjectsControl: FC<{
  control: Control<UserFormData>;
  setValue?: UseFormSetValue<UserFormData>;
  autoFocus?: boolean;
  excludeLabel?: boolean;
  onBlur?: () => void;
}> = ({ control, setValue, autoFocus, excludeLabel, onBlur }) => {
  const onBlurHandler = useDetailsInlineSaving(onBlur);
  const onMouseDownHandler = useOnMouseEventPreventDefaultHandler();

  const user = useWatch({ name: "user", control });
  const aggregatedUserIds = useGetAggregatedEntityIds(user);
  const aggregatedUsersCurrentProjectIds = useDeepRootSelector(state =>
    uniq(selectUsersByIds(state, aggregatedUserIds).map(({ current_project_id }) => current_project_id)),
  );

  const onChangeHandler = useCallback(
    (onChange: (inputData: Project[]) => void) => (inputData: Project[]) => {
      if (setValue) {
        setValue(
          "user.project_ids",
          inputData.map(({ id }) => id),
          {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          },
        );
      }

      onChange(inputData);
    },
    [setValue],
  );

  return (
    <Controller
      name="projects"
      control={control}
      render={({ field: { ref, value, onChange, onBlur: onBlurFn, ...restFields }, fieldState }) => {
        const element = (
          <AutoCompleteMultipleDecorator
            inputRef={ref}
            value={value}
            chipsMapper={(project: Project) => [
              `user-project-chip-${project.id}`,
              project.name,
              {
                isRemovable: !aggregatedUsersCurrentProjectIds.includes(project.id),
                template: <TextLink to={composeDetailsRouteByEntityType(project)}>{project.name}</TextLink>,
              },
            ]}
            onChange={onChangeHandler(onChange)}
            onBlur={onBlurHandler(onBlurFn)}
            onMouseDown={onMouseDownHandler}
            {...restFields}
          >
            {(props: ComponentProps<typeof ProjectsAutoComplete>) => (
              <ProjectsAutoComplete autoFocus={autoFocus} {...props} />
            )}
          </AutoCompleteMultipleDecorator>
        );

        if (excludeLabel) {
          return element;
        }

        return (
          <LabelWrapper label="Projects" className="col-8" error={fieldState.error}>
            {element}
          </LabelWrapper>
        );
      }}
    />
  );
};
